import type { AppDatabase } from './store';
import type { Game, GameResult, Id, ScheduleSlot, Team, Tournament } from '../models';
import { paths, tournamentSlug } from '../lib/paths';
import { normalizeSeasonSettings } from './tournamentService';

export interface LeagueSetupChecklistItem {
  id: string;
  label: string;
  detail: string;
  complete: boolean;
  href: string;
  actionLabel: string;
}

export interface LeagueSetupChecklist {
  items: LeagueSetupChecklistItem[];
  completed: number;
  total: number;
  percent: number;
}

export interface GameOperationGame {
  game: Game;
  tournament?: Tournament;
  home?: Team;
  away?: Team;
  slot?: ScheduleSlot;
  result?: GameResult;
  href: string;
  scheduleHref: string;
  operationsHref: string;
  publicHref?: string;
}

export interface GameOperationsSummary {
  todayGames: GameOperationGame[];
  upcomingGames: GameOperationGame[];
  needsScorekeeper: GameOperationGame[];
  needsOfficial: GameOperationGame[];
  readyToScore: GameOperationGame[];
  recentFinals: GameOperationGame[];
  standingsEvents: Tournament[];
}

const ACTIVE_EVENT_STATUSES: Tournament['status'][] = ['scheduled', 'in_progress', 'paused', 'completed'];

export function buildLeagueSetupChecklist(db: AppDatabase): LeagueSetupChecklist {
  const visibleEvents = db.tournaments
    .filter((tournament) => tournament.status !== 'archived')
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  const focusEvent = visibleEvents[0];
  const focusSlug = focusEvent ? tournamentSlug(focusEvent) : undefined;
  const hasEvent = db.tournaments.some((tournament) => tournament.status !== 'archived');
  const hasPublishedEvent = db.tournaments.some((tournament) => ACTIVE_EVENT_STATUSES.includes(tournament.status));
  const hasSchedule = db.games.length > 0 && db.scheduleSlots.length > 0;
  const hasTeam = db.teams.length > 0;
  const hasRegistrationIntake = db.tournaments.some((tournament) => {
    const season = normalizeSeasonSettings(tournament.settings.season);
    return season.registration.enabled || season.rosterIntake === 'registration';
  });
  const hasCommunication = db.communications.length > 0 || db.communicationThreadMessages.length > 0;
  const hasPaymentSetupSignal = db.paymentIntents.length > 0;
  const hasContactRoute = Boolean(db.league.contactEmail?.trim() || db.league.communicationSettings.replyToEmail?.trim());

  const items: LeagueSetupChecklistItem[] = [
    {
      id: 'league-profile',
      label: 'League profile',
      detail: hasContactRoute
        ? 'League identity and contact route are configured.'
        : 'Set the league name, public contact email, sport, and default divisions.',
      complete: Boolean(db.league.name.trim() && db.league.sport.trim() && hasContactRoute),
      href: paths.adminSettings,
      actionLabel: 'Open settings',
    },
    {
      id: 'communications',
      label: 'Communication settings',
      detail: hasContactRoute
        ? 'Outbound identity has a reply-to/contact address.'
        : 'Add a sender/reply-to address before sending league messages.',
      complete: hasContactRoute,
      href: paths.adminSettings,
      actionLabel: 'Configure comms',
    },
    {
      id: 'billing',
      label: 'Financials and payouts',
      detail: hasPaymentSetupSignal
        ? 'Payment records are flowing through the league.'
        : 'Connect Stripe before collecting registration payments.',
      complete: hasPaymentSetupSignal,
      href: paths.adminBilling,
      actionLabel: 'Open financials',
    },
    {
      id: 'first-event',
      label: 'First event',
      detail: hasEvent ? 'A season or tournament exists in League HQ.' : 'Create a season or tournament to start operating.',
      complete: hasEvent,
      href: paths.adminSeasonNew,
      actionLabel: 'New season',
    },
    {
      id: 'teams-registration',
      label: 'Teams or intake path',
      detail: hasTeam || hasRegistrationIntake
        ? 'Teams or registration intake are ready for an event.'
        : 'Add teams manually or turn on registration intake.',
      complete: hasTeam || hasRegistrationIntake,
      href: focusEvent && focusSlug ? paths.adminCompetitionTab(focusEvent.format, focusSlug, 'teams') : paths.adminSeasonNew,
      actionLabel: focusSlug ? 'Open teams' : 'New season',
    },
    {
      id: 'schedule',
      label: 'Schedule generated',
      detail: hasSchedule ? 'Games and rink slots are on the calendar.' : 'Generate a schedule once teams and rinks are ready.',
      complete: hasSchedule,
      href: focusEvent && focusSlug ? paths.adminCompetitionTab(focusEvent.format, focusSlug, 'schedule') : paths.adminSeasons,
      actionLabel: focusSlug ? 'Open schedule' : 'Open seasons',
    },
    {
      id: 'published',
      label: 'Event published',
      detail: hasPublishedEvent ? 'Players can see a published event.' : 'Publish when setup is complete.',
      complete: hasPublishedEvent,
      href: paths.adminTournaments,
      actionLabel: 'Review events',
    },
    {
      id: 'first-message',
      label: 'First communication',
      detail: hasCommunication ? 'At least one league message has been recorded.' : 'Send a targeted update after an event is published.',
      complete: hasCommunication,
      href: paths.adminComms,
      actionLabel: 'Open comms',
    },
  ];

  const completed = items.filter((item) => item.complete).length;
  return {
    items,
    completed,
    total: items.length,
    percent: Math.round((completed / items.length) * 100),
  };
}

export function buildGameOperationsSummary(db: AppDatabase, now: Date = new Date()): GameOperationsSummary {
  const visibleTournamentIds = new Set(db.tournaments.filter((tournament) => tournament.status !== 'archived').map((tournament) => tournament.id));
  const tournamentById = new Map(db.tournaments.map((tournament) => [tournament.id, tournament]));
  const teamById = new Map(db.teams.map((team) => [team.id, team]));
  const slotById = new Map(db.scheduleSlots.map((slot) => [slot.id, slot]));
  const resultByGameId = new Map(db.gameResults.map((result) => [result.gameId, result]));

  const hydrated = db.games
    .filter((game) => visibleTournamentIds.has(game.tournamentId))
    .map((game) => hydrateGameOperation(game, tournamentById, teamById, slotById, resultByGameId))
    .sort(compareGameOperationStart);

  const todayKey = dateKey(now);
  const nowMs = now.getTime();
  const openGames = hydrated.filter((item) => !['final', 'forfeit', 'cancelled'].includes(item.game.status));

  return {
    todayGames: hydrated.filter((item) => item.slot && dateKey(new Date(item.slot.startsAt)) === todayKey).slice(0, 12),
    upcomingGames: hydrated.filter((item) => {
      if (!item.slot) return false;
      const startsAt = new Date(item.slot.startsAt).getTime();
      return startsAt >= nowMs && item.game.status !== 'final' && item.game.status !== 'forfeit';
    }).slice(0, 12),
    needsScorekeeper: openGames.filter((item) => !db.gameAssignments.some((assignment) => assignment.gameId === item.game.id && assignment.position === 'scorekeeper')).slice(0, 12),
    needsOfficial: openGames.filter((item) => !db.gameAssignments.some((assignment) => assignment.gameId === item.game.id && assignment.position.startsWith('referee'))).slice(0, 12),
    readyToScore: openGames.filter((item) => item.game.status === 'ready' || item.game.status === 'scheduled' || item.game.status === 'in_progress').slice(0, 12),
    recentFinals: hydrated
      .filter((item) => item.game.status === 'final' || item.game.status === 'forfeit')
      .sort((a, b) => (b.result?.endedAt ?? b.game.updatedAt).localeCompare(a.result?.endedAt ?? a.game.updatedAt))
      .slice(0, 8),
    standingsEvents: db.tournaments
      .filter((tournament) => tournament.status !== 'archived')
      .filter((tournament) => db.teams.some((team) => team.tournamentId === tournament.id))
      .sort((a, b) => b.startsAt.localeCompare(a.startsAt))
      .slice(0, 6),
  };
}

function hydrateGameOperation(
  game: Game,
  tournamentById: Map<Id, Tournament>,
  teamById: Map<Id, Team>,
  slotById: Map<Id, ScheduleSlot>,
  resultByGameId: Map<Id, GameResult>,
): GameOperationGame {
  const tournament = tournamentById.get(game.tournamentId);
  const slug = tournament ? tournamentSlug(tournament) : game.tournamentId;
  return {
    game,
    tournament,
    home: teamById.get(game.homeTeamId),
    away: teamById.get(game.awayTeamId),
    slot: game.scheduleSlotId ? slotById.get(game.scheduleSlotId) : undefined,
    result: resultByGameId.get(game.id),
    href: paths.adminScoreGame(game.id),
    scheduleHref: tournament ? paths.adminCompetitionTab(tournament.format, slug, 'schedule') : paths.adminTournaments,
    operationsHref: tournament ? paths.adminCompetitionTab(tournament.format, slug, 'operations') : paths.adminTournaments,
    publicHref: tournament ? paths.event(tournament.slug) : undefined,
  };
}

function compareGameOperationStart(a: GameOperationGame, b: GameOperationGame): number {
  const aStart = a.slot?.startsAt ?? a.game.updatedAt;
  const bStart = b.slot?.startsAt ?? b.game.updatedAt;
  return aStart.localeCompare(bStart);
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
